import * as React from 'react';
import { MagnifyingGlassIcon, XIcon } from '@phosphor-icons/react';
import { cn } from '@/utils/utils';
import { FIELD_SIZES, SEARCH, fieldLook, type FieldLook, type FieldSize } from '@/components/ui/recipes';
import { IconButton } from '@/components/ui/icon-button';

interface SearchFieldProps extends Omit<React.ComponentProps<'input'>, 'size' | 'onChange' | 'value'> {
  value: string;
  onChange: (value: string) => void;
  /** md in forms and pages, sm in editor panels, xs in a compact bar (the timeline's). */
  size?: FieldSize;
  /** Filled or outlined; left out, its size's (FIELD_LOOKS). */
  look?: FieldLook;
  /** What sits inside the field after the text: the active filter as a removable chip. */
  trailing?: React.ReactNode;
  /** Shows a clear button once something is typed. */
  clearable?: boolean;
  /** Where the magnifier sits: start, or end for a panel's list search (the animation list), where the clear button takes its place once something is typed. */
  magnifier?: 'start' | 'end';
  /** For the field's box (its width, its place), where className styles the whole control. */
  className?: string;
}

// The magnifier's size and the room after it, at each field size
const ICON = { md: 'h-4 w-4', sm: 'h-4 w-4', xs: 'h-3.5 w-3.5' } as const;
const GAP = { md: 'gap-3', sm: 'gap-1.5', xs: 'gap-1.5' } as const;

/** Lureo's search: a pill field with its magnifier, what it filters by, and a clear button once something is typed. */
export const SearchField = React.forwardRef<HTMLInputElement, SearchFieldProps>(
  ({ value, onChange, size = 'sm', look, trailing, clearable = true, magnifier = 'start', className, placeholder = 'Search', ...props }, ref) => (
    <div className={cn(SEARCH, fieldLook(size, look), FIELD_SIZES[size], GAP[size], 'flex items-center focus-within:ring-2 focus-within:ring-lureo-accent/70 has-[input:disabled]:cursor-not-allowed has-[input:disabled]:opacity-lureo-disabled-opacity', magnifier === 'end' && 'px-3', className)}>
      {magnifier === 'start' && <MagnifyingGlassIcon className={cn('pointer-events-none shrink-0 text-lureo-ink-subtle', ICON[size])} />}
      <input
        ref={ref}
        type="search"
        autoComplete="off"
        value={value}
        placeholder={placeholder}
        onChange={event => onChange(event.target.value)}
        className="h-full min-w-0 flex-1 border-0 bg-transparent p-0 text-inherit outline-hidden placeholder:text-lureo-ink-subtle [&::-webkit-search-cancel-button]:hidden disabled:cursor-not-allowed"
        {...props}
      />
      {trailing}
      {clearable && value ? (
        <IconButton label="Clear search" onClick={() => onChange('')} className="-mr-1.5 h-6 w-6">
          <XIcon className="h-3.5 w-3.5" />
        </IconButton>
      ) : (
        magnifier === 'end' && <MagnifyingGlassIcon className={cn('pointer-events-none shrink-0 text-lureo-ink', ICON[size])} />
      )}
    </div>
  ),
);
SearchField.displayName = 'SearchField';
